/**
 * Page Components
 */
import { useState, useEffect } from "react";
import { validateUrl } from '../utils/UrlValidationService';
import { normalizeUrl } from '../utils/normalizeUrl';
import { getToken } from "firebase/app-check";
import { RefreshCcw, SquareArrowUpRight } from "lucide-react";

export const Scanner = ({isLight, appCheckInstance, onNavigate, user, auth}) => {
  const [url, setUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [engineStatus, setEngineStatus] = useState('checking');

  useEffect(() => {
    if(user == null){
      onNavigate("/login");
    }
    const checkHeartbeat = async () => {
      try {
        const response = await fetch('https://lucy-git-dev-gobbledy.vercel.app/api/proxy?path=heartbeat');
        if (response.ok) {
          setEngineStatus('active');
        } else {
          setEngineStatus('inactive');
        }
      } catch (error) {
        setEngineStatus('inactive');
      }
    };
    checkHeartbeat();
  }, [user]);

  const fetchWithRetry = async (urlToScan, retries = 0, backoff = 1000) => {
    let appCheckToken = "";
    let userToken = "";
    if (appCheckInstance) {
      try {
        console.log("Requesting App Check token...");
        // forceRefresh: true is useful for debugging to bypass local cache
        const result = await getToken(appCheckInstance, true);
        appCheckToken = result.token;
        console.log("App Check Token successfully fetched:", appCheckToken ? "TOKEN_RECEIVED" : "EMPTY_TOKEN");

        console.log("Requesting User Auth Token...");
        userToken = await auth.currentUser.getIdToken(true);
        console.log("User Auth Token successfully fetched:", userToken ? "TOKEN_RECEIVED" : "EMPTY_TOKEN");
        
        // if (appCheckToken) console.log("App Check Token Value:", appCheckToken);
        // if (userToken) console.log("User Token Value:", userToken);
        
      } catch (err) {
        console.error("App Check token acquisition failed error details:", err);
      }
    } else {
      console.warn("App Check instance is not initialized yet.");
    }

    try {
      const response = await fetch('https://lucy-git-dev-gobbledy.vercel.app/api/proxy?path=predict', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Only add Authorization if token exists. 
          // Do NOT add x-firebase-appcheck as it is not in your proxy's allowed headers.
          ...(appCheckToken && { 'X-Firebase-AppCheck': `${appCheckToken}` }),
          ...(userToken && {'Authorization': `Bearer ${userToken}`}),
        },
        body: JSON.stringify({ url: urlToScan }),
      });
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, backoff));
        return fetchWithRetry(urlToScan, retries - 1, backoff * 2);
      }
      throw err;
    }
  };

  const handleScan = async () => {
    const normalizedUrl = normalizeUrl(url);
    const validationError = validateUrl(normalizedUrl);
    if (validationError) {
      setError('[VALIDATION_ERROR]: ' + validationError);
      setResults(null);
      return;
    }

    setIsScanning(true);
    setResults(null);
    setError(null);

    try {
      const data = await fetchWithRetry(normalizedUrl);
      if (data && data.error) {
        setError(data.error);
        setIsScanning(false);
        console.log(data.error);
        return;
      }
      const cleanUrl = url.toLowerCase();
      // Adjusting score logic: confidence 1.0 = 100% risk if fraudulent, or 0% risk if legitimate
      // If is_fraudulent is true, risk score = confidence * 100.
      // If is_fraudulent is false, risk score = (1 - confidence) * 100.
      console.log(data);
      const riskScore = data.is_fraudulent 
        ? Math.round((1 - data.confidence) * 100) 
        : Math.round(data.confidence * 100);

      const isPremium = user?.tier?.type === "premium";

      if (!isPremium) {
        setResults({
          score: riskScore,
          isFraudulent: data.is_fraudulent,
          reasons: data.reasons,
          title: data.title,
        });
      } else {
        setResults({
          score: riskScore,
          isFraudulent: data.is_fraudulent,
          reasons: data.reasons,
          title: data.title,
          premium: data.premium
        });
      }
    } catch (err) {
      setError("SYSTEM ERROR: Failed to connect to the Lucy core. Please check your connection and try again.");
    } finally {
      setIsScanning(false);
    }
  };

   const clearInput = () => {
    setUrl('');
    setResults(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default form submission if any
      handleScan();
    }
  };

  const handleReScan = async () => {
      // Clear the UI first
    setResults(null);
    setError(null);
    
    setTimeout(() => {
      handleScan();
    }, 5);
  }

  const handleVisit = () => {
    // Logic here (e.g., track click)
    window.open(normalizeUrl(url), "_blank", "noopener,noreferrer");
  };

  const getStatusColor = (isFraudulent) => {
    if (isFraudulent){
      return isLight ? '#c20502' : '#ff0400';
    } 
    else{
      return isLight ? '#22c202':'#33ff00';
    }
    // if (score == 50) return '#ff3333';
    // return isLight ? '#059669' : '#00ffaa';
  };

  return (
    <div className={`rounded-lg p-10 md:p-14 border relative transition-all duration-500 animate-in shadow-2xl ${
      isLight ? 'bg-white/80 border-slate-200' : 'bg-[#111114]/80 border-white/10'} backdrop-blur-md`}>

      <div className="space-y-8">
        <div className="flex justify-start gap-2">
          <span className={`w-2 h-2 rounded-3xl ${
            engineStatus === 'active' ? 'bg-emerald-500' :
            engineStatus === 'inactive' ? 'bg-red-500' : 'bg-yellow-500'
          } shadow-[0_0_8px_rgba(16,185,129,0.5)]`}></span>
          <span className="text-slate-500 text-[9px] uppercase tracking-[0.2em] font-bold">
            {engineStatus === 'active' ? 'Engine Core: Active' :
             engineStatus === 'inactive' ? 'Engine Core: Inactive' :
             'Engine Core: Checking...'}
          </span>
          {!appCheckInstance && (
            <span className="text-amber-500 text-[9px] uppercase tracking-[0.2em] font-bold animate-pulse ml-2">
              (Initializing Security...)
            </span>
          )}
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-3 block font-bold">Target Input (URL)</label>
          <div className="relative group">
            <input 
              type="text" 
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="INPUT://SCAN-TARGET.COM"
              className={`w-full bg-transparent border-b py-3 pr-10 ${isLight ? "text-slate-900 focus:border-cyan-700 border-slate-400/60" : "text-white focus:border-white border-white/10"} outline-none transition-all font-mono text-base`}
            />
            {url && (
              <button 
                onClick={clearInput}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-slate-500 hover:text-white transition-colors"
                title="Clear input"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        <button 
          onClick={handleScan} 
          disabled={isScanning} 
          className={`w-full font-black py-5 rounded uppercase text-xs tracking-[0.15em] transition-all duration-300 ${
            isLight ? 'bg-slate-900 text-white hover:bg-slate-800' : 'bg-white text-black hover:bg-slate-200'
          } disabled:opacity-50`}
        >
          {isScanning ? 'Computing...' : 'Analyze Target'}
        </button>
        {error && (
          <div className="p-4 rounded border border-red-500/20 bg-red-500/5">
            <p className="text-red-500 text-[10px] uppercase font-bold text-center tracking-widest leading-relaxed">
              {error}
            </p>
          </div>
        )}
      </div>

      {results && (
        <div className={`mt-12 pt-8 border-t animate-in ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
          <div className="flex justify-between items-start mb-6">
            <div className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: getStatusColor(results.isFraudulent) }}>
                {results.isFraudulent ? 'SUSPICIOUS' :'SECURE'}
              </span>
              <h3 className={`text-lg font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
                <span className={`text-xs font-normal tracking-[0.1em] ${isLight ? 'text-slate-900' : 'text-slate-300'}`}>Page Title: </span>
                <br></br>
                {results.title || 'Unknown'}
              </h3>
            </div>
            <div className="text-right">
                <span className="block text-[8px] opacity-50 uppercase tracking-widest mb-1">AI Confidence</span>
                <span className="text-5xl font-mono font-black italic" style={{ color: getStatusColor(results.isFraudulent) }}>{results.score}%</span>
            </div>
          </div>
          
          <div className={`h-[3px] rounded-3xl overflow-hidden mb-6 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`}>
            <div className="h-full transition-all duration-1000" style={{ width: `${results.score}%`, backgroundColor: getStatusColor(results.isFraudulent) }}></div>
          </div>

          {/* Check if user is premium */}
          {user?.tier?.type === "premium" && results.premium ? (
            // 1. FULL PREMIUM VIEW
            <div className="animate-in">
              <div className="grid grid-cols-1 md:grid-cols-2 md:gap-2">
                <div className={`mb-2 p-4 rounded border font-mono text-[10px] leading-relaxed ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/5 border-white/10 text-slate-400'}`}>
                  <div>
                    <span className="uppercase font-semibold text-cyan-500">Domain Age:</span> <span>{results.premium.domain_age}</span>
                  </div>
                </div>
                <div className={`mb-2 p-4 rounded border font-mono text-[10px] leading-relaxed ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/5 border-white/10 text-slate-400'}`}>
                  <div>
                    <span className="uppercase font-semibold text-cyan-500">Location:</span> <span>{results.premium.location}</span>
                  </div>
                </div>
              </div>
              
              <div className={`mb-2 p-4 rounded border font-mono text-[10px] leading-relaxed ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/5 border-white/10 text-slate-400'}`}>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="mb-2">
                      <span className="uppercase text-cyan-500 mr-2 font-bold">SSL Certificate Info</span>
                    </div>
                    <div className="space-y-1">
                      <span className="uppercase block">Registered Domain: {results.premium.registered_domain_name}</span>
                      <span className="uppercase block">Issuer: {results.premium.ssl_issuer}</span>
                      <span className="uppercase block">Expiry: {results.premium.ssl_expiry}</span>
                    </div>
                  </div>
                  {/* Your Existing Visual Snapshot Code */}
                </div>
              </div>
            </div>
          ) : (
            // 2. GUEST / FREE PREVIEW (The Teaser)
            <div className="relative mt-4">
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/20 backdrop-blur-[6px] rounded-lg border border-dashed border-cyan-500/30">
                  <button 
                    onClick={() => onNavigate('/login')}
                    className="bg-cyan-500 text-black px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-cyan-400 transition-all shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                  >
                    Unlock Deep Intelligence
                  </button>
                  <span className="text-[8px] text-slate-400 mt-2 uppercase tracking-widest font-mono">Premium Membership Required</span>
              </div>

              {/* This is a "Dummy" version of the UI behind the blur */}
              <div className="opacity-20 grayscale select-none pointer-events-none">
                <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="h-10 bg-slate-800 rounded border border-white/10"></div>
                    <div className="h-10 bg-slate-800 rounded border border-white/10"></div>
                </div>
                <div className="h-24 bg-slate-800 rounded border border-white/10"></div>
              </div>
            </div>
          )}

          <div className={`p-4 rounded border font-mono text-[10px] leading-relaxed ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/5 border-white/10 text-slate-400'}`}>
            <span className="text-cyan-500 mr-2">REASONING:</span>
            {results.reasons}
          </div>
          <div className={`w-full mt-4 flex flex-row justify-center`}>
              <button className={`m-2 font-medium py-2 px-2 rounded text-[12px] tracking-[0.05em] transition-all duration-300 ${
                    isLight ? 'bg-sky-300 text-slate-800 hover:bg-sky-800 hover:text-white' : 'bg-sky-700 text-white hover:bg-slate-200 hover:text-black'
                  } disabled:opacity-50`}
                  onClick={handleReScan}
                  title="Rescan your target URL">
                <div className="flex flex-row justify-center">
                  <RefreshCcw size={14} className="mr-2" /> Rescan
                </div>
              </button>
              <button className={`m-2 font-medium py-2 px-2 rounded text-[12px] tracking-[0.05em] transition-all duration-300 ${
                    isLight ? 'bg-teal-300 text-slate-800 hover:bg-teal-800 hover:text-white' : 'bg-teal-600 text-white hover:bg-slate-200 hover:text-black'
                  } disabled:opacity-50`}
                  onClick={handleVisit}
                  title="Visit your target URL">
                <div className="flex flex-row justify-center">
                  <SquareArrowUpRight size={14} className="mr-2" /> Visit
                </div>
              </button>
            </div>
        </div>
      )}
    </div>
  );
};
